/**
 * Resolve when the {@link HTMLVideoElement} has painted its NEXT
 * frame. Used to gate sprite-visibility flips that depend on a
 * live video frame existing — without this, the seek-to-0 + decode
 * warm-up after {@code currentTime = 0} + {@code play()} leaves the
 * video texture as a 1×1 black placeholder and any downstream
 * sprite that flips visible during that window flashes black
 * through the cell.
 *
 * <p>Prefers {@code requestVideoFrameCallback} (Chromium / Safari
 * 15.4+ / Firefox 132+) which fires once per painted frame. Falls
 * back to the {@code playing} event on older browsers (less precise
 * — fires before the first frame composites — but better than
 * nothing).
 *
 * <p>Used by both {@link ../slot/SlotSymbol SlotSymbol} (per-symbol
 * win celebrations) and {@link ../chest/ChestWidget ChestWidget}
 * (chest-opening overlay). Lives in its own file so the two
 * unrelated widgets can share the impl without re-importing each
 * other.
 */
export function waitForNextVideoFrame(element: HTMLVideoElement): Promise<void> {
  type RVFCElement = HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: () => void) => void;
  };
  const rvfcEl = element as RVFCElement;
  if (typeof rvfcEl.requestVideoFrameCallback === 'function') {
    return new Promise<void>((resolve) => {
      rvfcEl.requestVideoFrameCallback!(() => resolve());
    });
  }
  return new Promise<void>((resolve) => {
    if (
      !element.paused &&
      element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
    ) {
      resolve();
      return;
    }
    element.addEventListener('playing', () => resolve(), { once: true });
  });
}
